import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import type { ReportHoldingValueMetadata } from './buildReportDisplayMetadata';

/** Pick live report holdings when trades have started settling; otherwise use run proposed end. */
export const resolveAuditHoldingDisplayCents = ({
  runTimeCurrentCents,
  deltaCents,
  reportValueCents,
}: {
  runTimeCurrentCents: number;
  deltaCents: number;
  reportValueCents?: number;
}): number => {
  const runTimeProposedEnd = Math.max(0, runTimeCurrentCents + deltaCents);

  if (reportValueCents == null) {
    return runTimeProposedEnd;
  }

  if (reportValueCents < runTimeCurrentCents) {
    return reportValueCents;
  }

  return runTimeProposedEnd;
};

/** Replace audit-reconstructed MV/weights with portfolio report holdings when available. */
export const applyReportHoldingsToDiffRows = (
  diffRows: WeightDiffDetailRow[],
  holdingsValuesByProductId: Map<string, ReportHoldingValueMetadata>,
  totalMarketValueCents: number,
): WeightDiffDetailRow[] =>
  diffRows.map((row) => {
    const reportHolding = holdingsValuesByProductId.get(row.financialProductId);
    const runTimeCurrentCents = row.currentCents;
    const runTimeProposedCents = row.proposedCents;

    if (!reportHolding) {
      return {
        ...row,
        runTimeCurrentCents,
        runTimeProposedCents,
        subAccountRows: row.subAccountRows.map((subAccountRow) => ({
          ...subAccountRow,
          runTimeCurrentCents: subAccountRow.currentCents,
          runTimeProposedCents: subAccountRow.proposedCents,
        })),
      };
    }

    const subAccountRows = row.subAccountRows.map((subAccountRow) => {
      const reportSubAccountValue = reportHolding.bySubAccount.get(
        subAccountRow.subAccountId,
      )?.valueCents;
      const subAccountRunTimeCurrent = subAccountRow.currentCents;
      const displayCurrentCents = resolveAuditHoldingDisplayCents({
        runTimeCurrentCents: subAccountRunTimeCurrent,
        deltaCents: subAccountRow.deltaCents,
        reportValueCents: reportSubAccountValue,
      });

      return {
        ...subAccountRow,
        runTimeCurrentCents: subAccountRunTimeCurrent,
        runTimeProposedCents: subAccountRow.proposedCents,
        currentCents: displayCurrentCents,
        proposedCents: displayCurrentCents,
      };
    });

    const currentCents = subAccountRows.reduce(
      (sum, subAccountRow) => sum + subAccountRow.currentCents,
      0,
    );

    const targetCents = reportHolding.expectedValueCents;

    return {
      ...row,
      runTimeCurrentCents,
      runTimeProposedCents,
      currentCents,
      proposedCents: currentCents,
      targetCents,
      targetPct:
        totalMarketValueCents > 0 ? targetCents / totalMarketValueCents : 0,
      modelTargetPct: reportHolding.expectedPercentage ?? row.modelTargetPct,
      currentPct:
        totalMarketValueCents > 0 ? currentCents / totalMarketValueCents : 0,
      proposedPct:
        totalMarketValueCents > 0 ? currentCents / totalMarketValueCents : 0,
      subAccountRows,
    };
  });

export const hasReportHoldingsValues = (
  holdingsValuesByProductId: Map<string, ReportHoldingValueMetadata>,
): boolean =>
  [...holdingsValuesByProductId.values()].some(
    (holding) =>
      holding.bySubAccount.size > 0 ||
      holding.valueCents > 0 ||
      holding.expectedValueCents > 0,
  );
